import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, LifeBuoy } from "lucide-react";
import { Header } from "@/components/Header";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Messages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const load = async () => {
    try { const { data } = await api.get("/messages"); setMessages(data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("load messages failed", e); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const requestHelp = async () => {
    try {
      const { data } = await api.post("/support/request");
      setMessages((m) => [...m, data.message]);
      toast.success("Request sent — our team will reach out here.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col px-6 py-10 md:px-10">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="overline">Support</p>
            <h1 className="font-display text-3xl tracking-tight md:text-4xl">Messages</h1>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">Chat directly with the Customize Studio team.</p>
          </div>
          <button data-testid="request-help-btn" onClick={requestHelp}
            className="flex shrink-0 items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-2.5 text-sm font-medium transition-transform hover:-translate-y-px">
            <LifeBuoy className="h-4 w-4 text-[var(--brand)]" /> Request library setup help
          </button>
        </div>

        <div className="thin-scroll mt-6 max-h-[60vh] min-h-[320px] flex-1 space-y-3 overflow-y-auto rounded-xl border border-[var(--line)] bg-white p-4">
          {messages.length === 0 && <p className="py-10 text-center text-sm text-[var(--ink-soft)]">No messages yet. Say hello or request setup help above.</p>}
          {messages.map((m) => {
            const mine = m.sender_role === "user";
            return (
              <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[var(--brand)] text-white" : "bg-[var(--surface-2)] text-[var(--ink)]"}`}>
                  {!mine && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.sender_name || "Support"}</p>}
                  <p className="whitespace-pre-wrap">{m.text}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-4 flex items-center gap-2">
          <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 rounded-full border border-[var(--line)] bg-white px-4 py-3 text-sm outline-none focus:border-[var(--brand)]" />
          <button type="submit" data-testid="send-message-btn" disabled={sending}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--brand)] text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
